import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Typography } from '@mui/material';
import { useGeolocation } from '../hooks/useGeolocation';

import { styled } from "@mui/system";
import {
  Box,
  Container,
  Grid,
  Paper,
  useTheme,
  Tabs,
  Tab,
  CircularProgress,
} from "@mui/material";
import { req, log } from "../utils"
import { useDataContext } from "../DataContext";
import { toast } from 'react-toastify';
import CustomerList    from "../components/CustomerList";
import VenueDetailsModal from "../components/VenueDetailsModal";
import LeadsList from "../components/LeadsList";
import MapView from "../components/MapView";


export const Dashboard = () => {
  const [selectedDateRange, setSelectedDateRange] = useState(null);
  const { dateRanges } = useDataContext();
  const [leads, setLeads] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);
  const [venues, setVenues] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const { location, error: locationError } = useGeolocation();

  const handleItemSelect = useCallback((item) => {
    setSelectedItem(item);
    setShowModal(true);
  }, []);

  const handleTabChange = useCallback((event, newValue) => {
    setActiveTab(newValue);
  }, []);

  // Memoize transformed venues data
  const transformedVenues = useMemo(() => {
    return venues?.length ? venues : [];
  }, [venues]);

  // Memoize transformed leads data
  const transformedLeads = useMemo(() => {
    return leads || [];
  }, [leads]);

  const getBottleCyclerVenues = useCallback(async (lat, lng) => {
    const queryParams = lat && lng ? `?lat=${lat}&lng=${lng}` : '';
    const response = await req('GET', `get_venues${queryParams}`);
    return response || [];
  }, []);

  const getGoogleLeads = useCallback(async (lat, lng) => {
    try {
      return await req('GET', `get_google_places_leads?lat=${lat}&lng=${lng}`);
    } catch (error) {
      console.error('Error fetching Google leads:', error);
      return [];
    }
  }, []);

  useEffect(() => {
    document.title = "BDN Lead Generation";

    const fetchData = async () => {
      if (!location?.lat || !location?.lng) {
        setIsLoading(false);
        return;
      }

      try {
        const [venueData, leadsData] = await Promise.all([
          getBottleCyclerVenues(location.lat, location.lng),
          getGoogleLeads(location.lat, location.lng)
        ]);

        setVenues(venueData);
        setLeads(leadsData);
      } catch (error) {
        console.error("Error fetching data:", error);
        toast.error("Failed to fetch venue data");
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [location, getBottleCyclerVenues, getGoogleLeads]);

  // Add this function inside the Dashboard component
  const handleNewLeads = useCallback((newLeads) => {
    //make sure we don't add duplicates
    const uniqueLeads = newLeads.filter(lead => !transformedLeads.some(l => l.id === lead.id));
    //add the new leads to the transformedLeads array
    setLeads([...transformedLeads, ...uniqueLeads]);
  }, [transformedLeads]);

  // Memoize the tab content components
  const TabContent = useMemo(() => ({
    Accounts: isLoading ? (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress sx={{ color: '#00A693' }} />
      </Box>
    ) : (
      <CustomerList 
        selectedItem={selectedItem}
        onItemClick={handleItemSelect}
        isLeads={false}
        venues={transformedVenues}
      />
    ),
    Leads: isLoading ? (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress sx={{ color: '#00A693' }} />
      </Box>
    ) : (
      <LeadsList 
        venues={transformedLeads} 
        onNewLeads={handleNewLeads}
      />
    ),
    MapView: (
      <MapView
        venues={transformedVenues}
        leads={transformedLeads}
        userLocation={location}
        onVenueClick={handleItemSelect}
      />
    )
  }), [isLoading, selectedItem, transformedVenues, transformedLeads, handleNewLeads, location, handleItemSelect]);

  // Render loading or error states
  const renderContent = () => {
    if (locationError) {
      return (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
          <Typography color="error">
            Please enable location services to use this application. {locationError}
          </Typography>
        </Box>
      );
    }

    if (isLoading && !location) {
      return (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
          <CircularProgress sx={{ color: '#00A693' }} />
        </Box>
      );
    }

    // Main content
    return (
      <main className="bc-page--content dashboard">
        <Box sx={{ borderBottom: 0, borderColor: 'divider', mb: 1.5, mt: 1.5 }}>
          <Tabs 
            value={activeTab} 
            onChange={handleTabChange}
            variant="fullWidth"
            sx={{ 
              width: '100%',
              '& .MuiTabs-indicator': {
                display: 'none',
              },
              '& .MuiTab-root': {
                margin: '0 4px',
                borderRadius: '25px',
                '&.Mui-selected': {
                  backgroundColor: '#00A693',
                  color: 'white',
                },
              }
            }}
          >
            <Tab label={`Accounts (${transformedVenues.length})`} />
            <Tab label={`Leads (${transformedLeads.length})`} />
            <Tab label="Map View" />
          </Tabs>
        </Box>

        <div style={{ 
          display: 'block',
          visibility: activeTab === 0 ? 'visible' : 'hidden',
          height: activeTab === 0 ? 'auto' : 0,
          overflow: 'hidden'
        }}>
          {TabContent.Accounts}
        </div>

        <div style={{ 
          display: 'block',
          visibility: activeTab === 1 ? 'visible' : 'hidden',
          height: activeTab === 1 ? 'auto' : 0,
          overflow: 'hidden'
        }}>
          {TabContent.Leads}
        </div>

        <div style={{ 
          display: 'block',
          visibility: activeTab === 2 ? 'visible' : 'hidden',
          height: activeTab === 2 ? 'auto' : 0,
          overflow: 'hidden'
        }}>
          {TabContent.MapView}
        </div>

        <VenueDetailsModal
          venue={selectedItem}
          open={showModal}
          onClose={() => setShowModal(false)}
        />
      </main>
    );
  };

  return renderContent();
};

export default Dashboard;
